import { useState, useEffect } from 'react';
import { getCompany, updateCompany } from '../../../entities/profile';
import { listUsers, createUser, updateUser, activateUser, deactivateUser } from '../../../entities/user';
import { toast } from 'react-hot-toast';

export function SystemSettingsPage() {
  const [activeTab, setActiveTab] = useState('company');

  // ---------- COMPANY STATE ----------
  const [formData, setFormData] = useState({
    company_name: '',
    phone_number: '',
    email_address: '',
    website: '',
    address: '',
    timezone: 'Eastern Time',
    company_logo: '',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // ---------- USER STATE ----------
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    first_name: '',
    last_name: '',
    email_address: '',
    user_type: 'Dispatcher',
    status: 'active',
  });

  // ---------- FETCH COMPANY ----------
  useEffect(() => {
    const fetchCompany = async () => {
      setLoading(true);
      try {
        const data = await getCompany();
        setFormData(data);
      } catch (err) {
        console.error('Error fetching company info:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCompany();
  }, []);

  // ---------- FETCH USERS ----------
  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    }
  }, [activeTab]);

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const res = await listUsers();
      setUsers(res.data || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      toast.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  // ---------- COMPANY FORM ----------
  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      setFormData({ ...formData, [name]: files[0]?.name || '' });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateCompany(formData);
      toast.success('Company information updated successfully!');
    } catch (err) {
      console.error('Error updating company:', err);
      toast.error('Failed to update company info');
    } finally {
      setSaving(false);
    }
  };

  // ---------- USER FORM ----------
  const handleUserFormChange = (e) => {
    const { name, value } = e.target;
    setUserForm({ ...userForm, [name]: value });
  };

  const handleSaveUser = async () => {
    try {
      if (editingUser) {
        await updateUser(editingUser.id, userForm);
        toast.success('User updated successfully!');
      } else {
        await createUser(userForm);
        toast.success('User created successfully!');
      }
      setShowUserModal(false);
      setEditingUser(null);
      setUserForm({ first_name: '', last_name: '', email_address: '', user_type: 'Dispatcher', status: 'active' });
      fetchUsers();
    } catch (err) {
      console.error('Error saving user:', err);
      toast.error('Failed to save user');
    }
  };

  const handleActivate = async (id) => {
    try {
      await activateUser(id);
      toast.success('User activated');
      fetchUsers();
    } catch (err) {
      console.error('Error activating user:', err);
      toast.error('Failed to activate user');
    }
  };

  const handleDeactivate = async (id) => {
    try {
      await deactivateUser(id);
      toast.success('User deactivated');
      fetchUsers();
    } catch (err) {
      console.error('Error deactivating user:', err);
      toast.error('Failed to deactivate user');
    }
  };

  // ---------- RENDER ----------
  if (loading) {
    return <p>Loading company information...</p>;
  }

  return (
    <div className="p-6 w-full space-y-6">
      <h2 className="text-xl font-semibold">System Settings</h2>

      {/* Tabs */}
      <div className="flex gap-2 bg-gray-100 rounded-lg p-1">
        {['company', 'users', 'notifications', 'fleet'].map((tab) => (
          <button key={tab} onClick={() => setActiveTab(tab)} className={`px-6 py-2 text-sm rounded-md capitalize ${activeTab === tab ? 'bg-white shadow font-medium' : 'text-gray-500'}`}>
            {tab === 'company' && 'Company'}
            {tab === 'users' && 'Users & Roles'}
            {tab === 'notifications' && 'Notifications'}
            {tab === 'fleet' && 'Fleet Settings'}
          </button>
        ))}
      </div>

      {/* ---------- COMPANY TAB ---------- */}
      {activeTab === 'company' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4">
          <h3 className="font-medium mb-2">Company Information</h3>

          {/* Company + Contact Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col space-y-1">
              <label className="text-sm font-medium text-gray-700">Company Name</label>
              <input name="company_name" value={formData.company_name} onChange={handleChange} placeholder="Company Name" className="border rounded-lg p-2 text-sm" />
            </div>
            <div className="flex flex-col space-y-1">
              <label className="text-sm font-medium text-gray-700">Phone Number</label>
              <input name="phone_number" value={formData.phone_number} onChange={handleChange} placeholder="Phone Number" className="border rounded-lg p-2 text-sm" />
            </div>
            <div className="flex flex-col space-y-1">
              <label className="text-sm font-medium text-gray-700">Email Address</label>
              <input name="email_address" value={formData.email_address} onChange={handleChange} placeholder="Email Address" className="border rounded-lg p-2 text-sm" />
            </div>
            <div className="flex flex-col space-y-1">
              <label className="text-sm font-medium text-gray-700">Website</label>
              <input name="website" value={formData.website} onChange={handleChange} placeholder="Website" className="border rounded-lg p-2 text-sm" />
            </div>
          </div>

          {/* Address */}
          <div className="flex flex-col space-y-1">
            <label className="text-sm font-medium text-gray-700">Address</label>
            <textarea name="address" value={formData.address} onChange={handleChange} placeholder="Address" className="border rounded-lg p-2 text-sm w-full" />
          </div>

          {/* Timezone + Logo Upload */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col space-y-1">
              <label className="text-sm font-medium text-gray-700">Timezone</label>
              <select name="timezone" value={formData.timezone} onChange={handleChange} className="border rounded-lg p-2 text-sm">
                <option>Eastern Time</option>
                <option>Pacific Time</option>
                <option>Central Time</option>
              </select>
            </div>
            <div className="flex flex-col space-y-1">
              <label className="text-sm font-medium text-gray-700">Company Logo</label>
              <input type="file" name="company_logo" onChange={handleChange} className="border rounded-lg p-2 text-sm" />
              {formData.company_logo && <p className="text-xs text-gray-500">Current: {formData.company_logo}</p>}
            </div>
          </div>

          {/* Submit Button */}
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-black text-white rounded-lg disabled:opacity-50">
            {saving ? 'Saving...' : 'Save Company Information'}
          </button>
        </div>
      )}

      {/* ---------- USERS TAB ---------- */}
      {activeTab === 'users' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-medium">User Management</h3>
              <button
                onClick={() => {
                  setEditingUser(null);
                  setUserForm({
                    first_name: '',
                    last_name: '',
                    email_address: '',
                    user_type: 'dispatcher',
                    status: 'active',
                  });
                  setShowUserModal(true);
                }}
                className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg"
              >
                + Add User
              </button>
            </div>

            {loadingUsers ? (
              <p>Loading users...</p>
            ) : (
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-4 py-2">Name</th>
                    <th className="px-4 py-2">Email</th>
                    <th className="px-4 py-2">Role</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-t">
                      <td className="px-4 py-2">
                        {user.first_name} {user.last_name}
                      </td>
                      <td className="px-4 py-2 text-blue-600">{user.email_address}</td>
                      <td className="px-4 py-2">
                        <span className="px-2 py-1 text-xs bg-gray-100 capitalize rounded">{user.user_type}</span>
                      </td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-1 text-xs rounded ${user.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'}`}>{user.status}</span>
                      </td>
                      <td className="px-4 py-2 flex gap-2">
                        <button
                          onClick={() => {
                            setEditingUser(user);
                            setUserForm({
                              first_name: user.first_name,
                              last_name: user.last_name,
                              email_address: user.email_address,
                              user_type: user.user_type,
                              status: user.status,
                            });
                            setShowUserModal(true);
                          }}
                          className="text-gray-600 hover:text-blue-600"
                        >
                          ✏️
                        </button>
                        {user.status === 'active' ? (
                          <button onClick={() => handleDeactivate(user.id)} className="text-red-600 hover:text-red-800">
                            Deactivate
                          </button>
                        ) : (
                          <button onClick={() => handleActivate(user.id)} className="text-green-600 hover:text-green-800">
                            Activate
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* ---------- USER MODAL ---------- */}
          {showUserModal && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl p-6 w-full max-w-md space-y-4">
                <h3 className="font-medium">{editingUser ? 'Edit User' : 'Add User'}</h3>
                <div className="flex flex-col space-y-2">
                  <label className="text-sm font-medium">First Name</label>
                  <input name="first_name" value={userForm.first_name} onChange={handleUserFormChange} className="border rounded-lg p-2 text-sm" />

                  <label className="text-sm font-medium">Last Name</label>
                  <input name="last_name" value={userForm.last_name} onChange={handleUserFormChange} className="border rounded-lg p-2 text-sm" />

                  <label className="text-sm font-medium">Email Address</label>
                  <input name="email_address" value={userForm.email_address} onChange={handleUserFormChange} className="border rounded-lg p-2 text-sm" />

                  <label className="text-sm font-medium">Role</label>
                  <select name="user_type" value={userForm.user_type} onChange={handleUserFormChange} className="border rounded-lg p-2 text-sm">
                    <option>Admin</option>
                    <option>Dispatcher</option>
                    <option>Driver</option>
                  </select>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <button onClick={() => setShowUserModal(false)} className="px-4 py-2 rounded-lg border">
                    Cancel
                  </button>
                  <button onClick={handleSaveUser} className="px-4 py-2 bg-blue-600 text-white rounded-lg">
                    {editingUser ? 'Save Changes' : 'Create User'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ---------- Notifications + Fleet stay same ---------- */}
      {activeTab === 'notifications' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4">
          <h3 className="font-medium">Notification Settings</h3>
          {['Email Notifications', 'SMS Notifications', 'Load Status Updates', 'Driver Alerts', 'System Maintenance', 'Weekly Reports'].map((setting, idx) => (
            <div key={idx} className="flex justify-between items-center border-b py-3">
              <div>
                <p className="font-medium">{setting}</p>
                <p className="text-xs text-gray-500">Receive {setting.toLowerCase()}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" defaultChecked={idx !== 2} />
                <div className="w-11 h-6 bg-gray-200 peer-checked:bg-blue-600 rounded-full peer transition"></div>
              </label>
            </div>
          ))}
          <button className="px-4 py-2 bg-black text-white rounded-lg">Save Notification Settings</button>
        </div>
      )}

      {activeTab === 'fleet' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4">
          <h3 className="font-medium">Fleet Settings</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input placeholder="Maximum Load Weight (lbs)" defaultValue="80000" className="border rounded-lg p-2 text-sm" />
            <input placeholder="Average Fuel Cost ($/gallon)" defaultValue="3.45" className="border rounded-lg p-2 text-sm" />
            <select className="border rounded-lg p-2 text-sm">
              <option>24/7 Operations</option>
              <option>Business Hours</option>
            </select>
            <select className="border rounded-lg p-2 text-sm">
              <option>Every 5 minutes</option>
              <option>Every 15 minutes</option>
            </select>
          </div>
          <textarea placeholder="Safety Requirements" className="border rounded-lg p-2 text-sm w-full" />
          <div className="space-y-2">
            {['Delay alerts (>30 minutes)', 'Maintenance reminders', 'License expiration alerts'].map((item, idx) => (
              <div key={idx} className="flex justify-between items-center">
                <p className="text-sm">{item}</p>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" defaultChecked />
                  <div className="w-11 h-6 bg-gray-200 peer-checked:bg-blue-600 rounded-full peer transition"></div>
                </label>
              </div>
            ))}
          </div>
          <button className="px-4 py-2 bg-black text-white rounded-lg">Save Fleet Settings</button>
        </div>
      )}
    </div>
  );
}
